(function () {
  /** Any form with this data attribute gets validation + API submit. Reuse snippet with any form class. */
  const FORM_SELECTOR = 'form[data-book-free-demo-form]';
  const MESSAGE_PHONE_CALL_EXISTS = 'Phone Call already exists.';
  const STORAGE_KEY_BOOK_DEMO_OTP = 'book_demo_otp';
  const RESEND_OTP_COOLDOWN_SEC = 60;
  const resendTimerIntervals = new WeakMap();

  const getRecaptchaToken = (form) => {
    if (!form) return Promise.resolve(null);
    const enabled = form.dataset.recaptchaEnabled === 'true';
    const action = form.dataset.recaptchaAction || 'submit';
    // Do not request a token if disabled in the Shopify section.
    if (!enabled) {
      return Promise.resolve(null);
    }
    if (window.KentRecaptcha && typeof window.KentRecaptcha.getToken === 'function') {
      return window.KentRecaptcha.getToken(action);
    }
    return Promise.resolve(null);
  };

  /** Generate a random 6-digit OTP. */
  const generateOtp = () => String(Math.floor(100000 + Math.random() * 900000));
  const getStoredOtp = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY_BOOK_DEMO_OTP);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  };
  const setStoredOtp = (otp, mobile) => {
    try {
      localStorage.setItem(STORAGE_KEY_BOOK_DEMO_OTP, JSON.stringify({
        otp: String(otp),
        mobile: String(mobile || ''),
        sentAt: Date.now()
      }));
    } catch (e) {}
  };
  const clearStoredOtp = () => {
    try {
      localStorage.removeItem(STORAGE_KEY_BOOK_DEMO_OTP);
    } catch (e) {}
  };

  const formatDateTime = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const clearErrors = (form) => {
    if (!form) return;
    form.querySelectorAll('[data-partner-form-error]').forEach((el) => {
      el.textContent = '';
    });
    form.querySelectorAll('[aria-invalid]').forEach((el) => {
      el.removeAttribute('aria-invalid');
    });
  };

  /** custom: from span data-partner-form-error — when set, that message is shown instead of default. */
  const getMsg = (custom, label, type, extra) => {
    const lbl = (label || '').replace(/\*$/, '').trim();
    const lblLower = lbl ? lbl.toLowerCase() : '';
    extra = extra || {};
    const { min, max } = extra;
    const useCustom = custom && String(custom).trim();
    if (type === 'required') {
      return useCustom ? String(custom).trim() : (lbl ? lbl + ' is required.' : 'This field is required.');
    }
    if (type === 'email') {
      return useCustom ? String(custom).trim() : (lbl ? lbl + ' must be a valid email address.' : 'Please enter a valid email address.');
    }
    if (type === 'select') {
      return useCustom ? String(custom).trim() : (lbl ? 'Please select ' + lblLower + '.' : 'Please select an option.');
    }
    if (type === 'min_chars') {
      const minC = min || 3;
      return lbl ? lbl + ' should be a minimum of ' + minC + ' characters.' : 'Should be a minimum of ' + minC + ' characters.';
    }
    if (type === 'max_chars') {
      const maxC = max || 100;
      return lbl ? lbl + ' should be a maximum of ' + maxC + ' characters.' : 'Should be a maximum of ' + maxC + ' characters.';
    }
    if (type === 'min_digits') {
      const minD = min || 10;
      return 'Please enter ' + minD + ' digit contact number.';
    }
    if (type === 'max_digits') {
      const maxD = max || 15;
      return 'Please enter maximum ' + maxD + ' digit contact number.';
    }
    if (type === 'no_alpha') {
      return useCustom ? String(custom).trim() : (lbl ? lbl + ' does not allow letters.' : 'Letters are not allowed.');
    }
    if (type === 'no_number') {
      return useCustom ? String(custom).trim() : (lbl ? lbl + ' does not allow numbers.' : 'Numbers are not allowed.');
    }
    return lbl ? 'Please enter ' + lblLower + '.' : 'Please enter this field.';
  };

  const validateField = (form, block, errEl, input) => {
    if (!errEl) return true;

    const customMsg = (errEl.getAttribute('data-partner-form-error') || '').trim();
    if (input && input.getAttribute('type') === 'checkbox') {
      const required = input.hasAttribute('required');
      if (required && !input.checked) {
        errEl.textContent = getMsg(customMsg, input.getAttribute('data-label'), 'required');
        input.setAttribute('aria-invalid', 'true');
        return false;
      }
      errEl.textContent = '';
      input.removeAttribute('aria-invalid');
      return true;
    }

    if (input && input.tagName !== 'SELECT') {
      const val = (input.value || '').trim();
      const inputType = (input.getAttribute('data-input-type') || input.getAttribute('type') || '').toLowerCase();
      const required = input.hasAttribute('required');

      if (!val) {
        if (required) {
          errEl.textContent = getMsg(customMsg, input.getAttribute('data-label'), 'required');
          input.setAttribute('aria-invalid', 'true');
          return false;
        }
        errEl.textContent = '';
        input.removeAttribute('aria-invalid');
        return true;
      }

      if (inputType === 'email') {
        const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRe.test(val)) {
          errEl.textContent = getMsg(customMsg, input.getAttribute('data-label'), 'email');
          input.setAttribute('aria-invalid', 'true');
          return false;
        }
      }

      const minC = parseInt(input.getAttribute('data-min-chars') || '0', 10) || 0;
      const maxC = parseInt(input.getAttribute('data-max-chars') || '0', 10) || 0;
      const checkLen = inputType === 'tel' ? val.replace(/\D/g, '').length : val.length;
      const msgTypeMin = inputType === 'tel' ? 'min_digits' : 'min_chars';
      const msgTypeMax = inputType === 'tel' ? 'max_digits' : 'max_chars';
      if (minC > 0 && checkLen < minC) {
        errEl.textContent = getMsg(customMsg, input.getAttribute('data-label'), msgTypeMin, { min: minC });
        input.setAttribute('aria-invalid', 'true');
        return false;
      }
      if (maxC > 0 && checkLen > maxC) {
        errEl.textContent = getMsg(customMsg, input.getAttribute('data-label'), msgTypeMax, { max: maxC });
        input.setAttribute('aria-invalid', 'true');
        return false;
      }

      if (input.getAttribute('data-not-allow-alpha') === 'true' && /[a-zA-Z]/.test(val)) {
        const noAlphaLbl = (input.getAttribute('data-label') || '').replace(/\*$/, '').trim();
        errEl.textContent = getMsg(customMsg, noAlphaLbl, 'no_alpha');
        input.setAttribute('aria-invalid', 'true');
        return false;
      }
      if (input.getAttribute('data-not-allow-number') === 'true' && /\d/.test(val)) {
        const noNumLbl = (input.getAttribute('data-label') || '').replace(/\*$/, '').trim();
        errEl.textContent = getMsg(customMsg, noNumLbl, 'no_number');
        input.setAttribute('aria-invalid', 'true');
        return false;
      }

      const customRegexStr = (input.getAttribute('data-custom-regex') || '').trim();
      if (customRegexStr && val) {
        try {
          const customRe = new RegExp(customRegexStr);
          if (!customRe.test(val)) {
            const regexErr = (input.getAttribute('data-custom-regex-error') || '').trim();
            const regexLbl = (input.getAttribute('data-label') || '').replace(/\*$/, '').trim();
            errEl.textContent = customMsg || regexErr || (regexLbl ? regexLbl + ' is invalid.' : 'Invalid format.');
            input.setAttribute('aria-invalid', 'true');
            return false;
          }
        } catch (e) {
          // ignore invalid regex
        }
      }

      errEl.textContent = '';
      input.removeAttribute('aria-invalid');
      return true;
    }

    if (input && input.tagName === 'SELECT') {
      const selVal = (input.value || '').trim();
      if (input.hasAttribute('required') && !selVal) {
        errEl.textContent = getMsg(customMsg, input.getAttribute('data-label'), 'select');
        input.setAttribute('aria-invalid', 'true');
        return false;
      }
      errEl.textContent = '';
      input.removeAttribute('aria-invalid');
      return true;
    }

    return true;
  };

  const runFormValidation = (form) => {
    let isValid = true;
    form.querySelectorAll('[data-partner-form-error]').forEach((errEl) => {
      const block = errEl.closest('.form-block');
      if (!block) return;
      const input = block.querySelector('input:not([type="radio"]), select, textarea');
      if (!validateField(form, block, errEl, input)) {
        isValid = false;
      }
    });
    return isValid;
  };


  const BOOK_DEMO_ENDPOINT = 'phone';
  const getBookDemoApiConfig = () => {
    const baseUrl = (typeof window.getAPIUrl === 'function') ? window.getAPIUrl('baseUrl') : '';
    const key = (typeof window.getAPIUrl === 'function') ? window.getAPIUrl('key') : '';
    const apiUrl = baseUrl ? (baseUrl.replace(/\/?$/, '/') + BOOK_DEMO_ENDPOINT.replace(/^\//, '')) : '';
    return { apiUrl, key };
  };

  /** Build payload; fields with data-allow-query go in queryParams, rest in bodyPayload. */
  const buildFormPayload = (form) => {
    const queryParams = {};
    const bodyPayload = {};
    form.querySelectorAll('input[name], select[name], textarea[name]').forEach((el) => {
      const name = el.getAttribute('name');
      if (!name) return;
      const value = el.type === 'checkbox'
        ? (el.checked ? 'true' : 'false')
        : (el.value || '').trim();
      if (el.getAttribute('data-allow-query') === 'true') {
        queryParams[name] = value;
      } else {
        bodyPayload[name] = value;
      }
      if (el.tagName === 'SELECT') {
        const selectedOption = el.options[el.selectedIndex];
        const productId = selectedOption && selectedOption.getAttribute('productID');
        if (productId) bodyPayload['Productid'] = productId;
      }
    });
    return { queryParams, bodyPayload };
  };

  const getApiMessageEl = (form) => form.querySelector('[data-book-demo-api-message]');
  const showApiMessage = (form, text, type = 'success') => {
    const el = getApiMessageEl(form);
    if (!el) return;
    el.classList.remove('partner-form__api-message--success', 'partner-form__api-message--error');
    el.classList.add(type === 'error' ? 'partner-form__api-message--error' : 'partner-form__api-message--success');
    el.textContent = text;
    el.style.display = 'block';
  };
  const hideApiMessage = (form) => {
    const el = getApiMessageEl(form);
    if (el) {
      el.style.display = 'none';
      el.textContent = '';
      el.classList.remove('partner-form__api-message--success', 'partner-form__api-message--error');
    }
  };

  const handleSubmit = (e) => {
    const form = e.target;
    if (!form || form.tagName.toLowerCase() !== 'form' || !form.matches(FORM_SELECTOR)) return;
    // =========================================
    // Honeypot spam protection
    // =========================================
      const honeypot = form.querySelector('[data-book-demo-honeypot]');
      if (honeypot && honeypot.value.trim() !== '') {
        e.preventDefault();
        e.stopPropagation();
        console.warn('[Book Free Demo] Spam submission blocked.');
        // Stop processing this submission
        return;
      }
    e.preventDefault();
    e.stopPropagation();

    if (form.dataset.submitting === 'true') return;

    clearErrors(form);

    if (!runFormValidation(form)) {
      const firstInvalid = form.querySelector('[aria-invalid]');
      if (firstInvalid && typeof firstInvalid.focus === 'function') {
        firstInvalid.focus();
      }
      return;
    }

    form.dataset.submitting = 'true';

    const submitBtn = form.querySelector('button[type="submit"]');
    // const otpSection = form.querySelector('[data-book-demo-otp-section]');  // not needed now
    // const step1 = form.querySelector('[data-book-demo-step="1"]');          // not needed now

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
      submitBtn.classList.add('loading');
    }
    hideApiMessage(form);

    // ─── OTP BYPASSED ────────────────────────────────────────────────────────
    // OTP flow is preserved in sendOtpRequest / handleVerifyOtpClick / etc.
    // Re-enable by replacing the submitBookDemoApi call below with sendOtpRequest
    // and restoring the OTP section show/hide logic.
    // ─────────────────────────────────────────────────────────────────────────
    getRecaptchaToken(form).then((recaptchaToken) => {
      submitBookDemoApi(form, {
        loadingButton: submitBtn,
        recaptchaToken: recaptchaToken,
        onSuccess: (f, resultType) => {
          form.dataset.submitting = 'false';
          if (resultType === 'success') {
              sessionStorage.setItem('thank_you_msg', 'Phone Call created successfully.');
              sessionStorage.setItem('formSubmitted', 'true');
              setTimeout(() => {
                  window.location.href = '/pages/thank-you';
              }, 300);

            // sessionStorage.setItem('thank_you_msg', 'Phone Call created successfully.');
            // setTimeout(() => {
            //   window.location.href = '/pages/thank-you';
            // }, 300);
          }
        }
      });
    });

  };

  /** Call phone/book-demo API with form data. options: { loadingButton, recaptchaToken, onSuccess(form) }. */
  const submitBookDemoApi = (form, options = {}) => {
    const { loadingButton, onSuccess, recaptchaToken } = options;
    const { apiUrl, key } = getBookDemoApiConfig();
    if (!apiUrl) {
      showApiMessage(form, 'API is not configured.', 'error');
      if (typeof onSuccess === 'function') onSuccess(form, 'error');
      return;
    }
    if (loadingButton) {
      loadingButton.disabled = true;
      loadingButton.setAttribute('aria-busy', 'true');
      loadingButton.classList.add('loading');
    }
    hideApiMessage(form);
    const { queryParams, bodyPayload } = buildFormPayload(form);
    const urlParams = new URLSearchParams(window.location.search);
    bodyPayload.AppointmentDateTime = formatDateTime();
    bodyPayload.Email = '';
    bodyPayload.State = '';
    bodyPayload.Leadtype = 'Customer';
    bodyPayload.leadsource = urlParams.get('utm_source') || 'website';
    bodyPayload.CampaignSource = urlParams.get('CampaignSource') || '';
    bodyPayload.utm_campaign = urlParams.get('utm_campaign') || '';
    bodyPayload.utm_content = urlParams.get('utm_content') || '';
    bodyPayload.utm_term = urlParams.get('utm_term') || '';
    bodyPayload.utm_medium = urlParams.get('utm_medium') || '';
    bodyPayload.utm_source = urlParams.get('utm_source') || '';

    let requestUrl = apiUrl;
    if (Object.keys(queryParams).length > 0) {
      const queryString = Object.keys(queryParams)
        .map((k) => encodeURIComponent(k) + '=' + encodeURIComponent(queryParams[k]))
        .join('&');
      requestUrl = apiUrl + (apiUrl.indexOf('?') !== -1 ? '&' : '?') + queryString;
    }
    const headers = { 'Content-Type': 'application/json' };
    if (key) headers['x-api-key'] = key;

    // reCAPTCHA v3 token, if the site-wide helper produced one. Sent alongside
    // websiteLeadInfo (not nested inside it) — adjust to match whatever shape
    // your /phone endpoint expects the token in.
    const requestBody = { websiteLeadInfo: bodyPayload };
    if (recaptchaToken) {
      requestBody['g-recaptcha-response'] = recaptchaToken;
      requestBody['recaptcha_action'] = form.dataset.recaptchaAction || 'submit';
    }
    if (window.KentRecaptcha && window.KentRecaptcha.debug) {
      console.log('[Book Free Demo][reCAPTCHA]', recaptchaToken ? 'Token WILL be sent with this submission.' : 'No token — submitting WITHOUT g-recaptcha-response.');
    }

    fetch(requestUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    })
      .then((res) => res.json().catch(() => null).then((data) => {
        if (!res.ok) {
          const msg = (data && data.message) ? data.message : 'Something went wrong. Please try again.';
          throw new Error(msg);
        }
        // if (data && data.success === false) {
        //   const msg = (data.message && String(data.message).trim()) || 'Something went wrong. Please try again.';
        //   showApiMessage(form, msg, 'error');
        //   if (typeof onSuccess === 'function') onSuccess(form, 'error');
        //   return;
        // }
        // const apiMessage = (data && data.message && String(data.message).trim()) || '';
        // if (apiMessage === MESSAGE_PHONE_CALL_EXISTS) {
        //   showApiMessage(form, MESSAGE_PHONE_CALL_EXISTS, 'error');
        //   if (typeof onSuccess === 'function') onSuccess(form, 'error');
        //   return;
        // }
        if (data && data.success === false) {
          const msg = (data.message && String(data.message).trim()) || 'Something went wrong. Please try again.';
          // Check for duplicate record
          if (msg.indexOf('Duplicate Record Exist') !== -1) {
            showApiMessage(form, 'Mobile number already exists. Please try with a different number.', 'error');
          } else {
            showApiMessage(form, msg, 'error');
          }
          
          if (typeof onSuccess === 'function') onSuccess(form, 'error');
          return;
        }
        const apiMessage = (data && data.message && String(data.message).trim()) || '';
        // Check for duplicate record from API response
        if (apiMessage.indexOf('Duplicate Record Exist') !== -1 || apiMessage === MESSAGE_PHONE_CALL_EXISTS) {
          showApiMessage(form, 'Mobile number already exists. Please try with a different number.', 'error');
          if (typeof onSuccess === 'function') onSuccess(form, 'error');
          return;
        }
        const successMsg = apiMessage || "Thank you for your interest. We'll review your details and contact you shortly.";
        showApiMessage(form, successMsg, 'success');
        form.removeAttribute('data-book-demo-otp-verified');
        if (typeof onSuccess === 'function') onSuccess(form, 'success');
        form.reset();
        clearErrors(form);
      }))
      .catch((err) => {
        showApiMessage(form, (err && err.message) ? err.message : 'Something went wrong. Please try again.', 'error');
        if (typeof onSuccess === 'function') onSuccess(form, 'error');
      })
      .finally(() => {
        form.dataset.submitting = 'false';
        if (loadingButton) {
          loadingButton.disabled = false;
          loadingButton.removeAttribute('aria-busy');
          loadingButton.classList.remove('loading');
          loadingButton.dataset.submitting = 'false';
        }
      });
  };

  const runFieldValidation = (e) => {
    const target = e.target;
    const form = target.closest(FORM_SELECTOR);
    const block = form ? target.closest('.form-block') : null;
    if (!block || !form || !target.matches('input, select, textarea')) return;
    const errEl = block.querySelector('[data-partner-form-error]');
    const input = block.querySelector('input:not([type="radio"]), select, textarea');
    if (!errEl || input !== target) return;
    validateField(form, block, errEl, input);
  };

  /** Restrict input in real time: strip disallowed characters (alpha or digits). */
  const handleRestrictInput = (e, dataAttr, regex) => {
    const target = e.target;
    if (!target || !target.matches('input, textarea') || target.type === 'radio') return;
    if (!target.closest(FORM_SELECTOR) || target.getAttribute(dataAttr) !== 'true') return;
    const val = target.value || '';
    const stripped = val.replace(regex, '');
    if (val !== stripped) {
      const start = target.selectionStart;
      target.value = stripped;
      const pos = Math.min(start, stripped.length);
      target.setSelectionRange(pos, pos);
    }
  };

  const getOtpApiConfig = () => {
    if (typeof window.getAPIUrl !== 'function') return { url: '', key: '' };
    return {
      url: window.getAPIUrl('otpGatewayUrl') || '',
      key: window.getAPIUrl('otpKey') || ''
    };
  };

  /** Call OTP API (send OTP to mobile). Returns Promise. */
  const sendOtpRequest = (form) => {
    const mobileInput = form.querySelector('[data-book-demo-mobile]');
    const mobile = mobileInput ? String(mobileInput.value || '').replace(/\D/g, '') : '';
    if (!mobile || mobile.length < 10) return Promise.reject(new Error('Invalid mobile'));
    const { url: otpBaseUrl, key: otpKey } = getOtpApiConfig();
    if (!otpBaseUrl) return Promise.reject(new Error('OTP service is not configured.'));
    const otp = generateOtp();
    setStoredOtp(otp, mobile);
    const msg = 'Dear Customer, OTP is ' + otp + ' for your enquiry on KENT https://www.kent.co.in';
    const params = new URLSearchParams({
      send_to: mobile,
      msg: msg
    });
    const url = otpBaseUrl.replace(/\/?$/, '') + '?' + params.toString();
    const headers = otpKey ? { 'x-api-key': otpKey } : {};
    return fetch(url, { method: 'GET', headers })
      .then((res) => res.text().then((text) => {
        try {
          return text ? JSON.parse(text) : {};
        } catch (e) {
          return { status: text };
        }
      }));
  };

  const updateVerifyOtpButtonState = (form) => {
    const otpInput = form.querySelector('[data-book-demo-otp-input]');
    const verifyOtpBtn = form.querySelector('[data-book-demo-verify-otp]');
    if (!otpInput || !verifyOtpBtn) return;
    const otp = String(otpInput.value || '').replace(/\D/g, '');
    verifyOtpBtn.disabled = otp.length < 6;
  };

  const startResendOtpTimer = (form) => {
    const timerEl = form.querySelector('[data-book-demo-otp-timer]');
    const resendLink = form.querySelector('[data-book-demo-resend-otp]');
    if (!timerEl || !resendLink) return;
    const clearExisting = () => {
      const id = resendTimerIntervals.get(form);
      if (id) {
        clearInterval(id);
        resendTimerIntervals.delete(form);
      }
    };
    clearExisting();
    resendLink.setAttribute('aria-disabled', 'true');
    resendLink.setAttribute('tabindex', '-1');
    resendLink.classList.add('link-disabled');
    resendLink.style.pointerEvents = 'none';
    resendLink.style.opacity = '0.6';
    let sec = RESEND_OTP_COOLDOWN_SEC;
    const update = () => {
      if (sec <= 0) {
        clearExisting();
        timerEl.textContent = '';
        resendLink.removeAttribute('aria-disabled');
        resendLink.removeAttribute('tabindex');
        resendLink.classList.remove('link-disabled');
        resendLink.style.pointerEvents = '';
        resendLink.style.opacity = '';
        return;
      }
      timerEl.textContent = 'in ' + sec + ' second' + (sec === 1 ? '' : 's');
      sec--;
    };
    update();
    const id = setInterval(update, 1000);
    resendTimerIntervals.set(form, id);
  };

  // OTP is currently bypassed by the form's direct-submit workflow.
  // Keep the handler inert so the resend link cannot accidentally create an API lead.
  const handleResendOtpClick = (e) => {
    const link = e.target.closest('[data-book-demo-resend-otp]');
    if (!link) return;
    e.preventDefault();
    e.stopPropagation();
  };

  const handleVerifyOtpClick = (e) => {
    const btn = e.target.closest('[data-book-demo-verify-otp]');
    if (!btn) return;
    
    // Check loading flags to prevent rapid double-clicks throwing "Invalid OTP" errors
    if (btn.disabled || btn.dataset.submitting === 'true' || btn.getAttribute('aria-busy') === 'true') return;
    
    const form = btn.closest(FORM_SELECTOR);
    if (!form) return;
    
    btn.dataset.submitting = 'true'; // Lock execution
    
    const otpInput = form.querySelector('[data-book-demo-otp-input]');
    const otpError = form.querySelector('[data-book-demo-otp-error]');
    const enteredOtp = otpInput ? String(otpInput.value || '').replace(/\D/g, '') : '';
    if (otpError) otpError.textContent = '';
    
    if (!enteredOtp || enteredOtp.length !== 6) {
      if (otpError) otpError.textContent = 'Please enter a valid 6-digit OTP.';
      btn.dataset.submitting = 'false'; // Unlock execution
      return;
    }
    
    const stored = getStoredOtp();
    if (!stored || stored.otp !== enteredOtp) {
      if (otpError) otpError.textContent = 'Invalid OTP. Please try again.';
      btn.dataset.submitting = 'false'; // Unlock execution
      return;
    }
    
    clearStoredOtp();
    form.setAttribute('data-book-demo-otp-verified', 'true');
    const otpSection = form.querySelector('[data-book-demo-otp-section]');
    const step1 = form.querySelector('[data-book-demo-step="1"]');
    hideApiMessage(form);
    
    // submitBookDemoApi(form, {
    //   loadingButton: btn,
    //   onSuccess: (f, resultType) => {
    //     const os = f.querySelector('[data-book-demo-otp-section]');
    //     const s1 = f.querySelector('[data-book-demo-step="1"]');
    //     if (resultType === 'success') {
    //       // Success state: keep only success message visible.
    //       if (os) os.style.display = 'none';
    //       if (s1) s1.style.display = 'none';
    //     } else {
    //       // Error state: return user to form step for correction/resubmit.
    //       if (os) os.style.display = 'none';
    //       if (s1) s1.style.display = '';
    //     }
    //   }
    // });

    getRecaptchaToken(form).then((recaptchaToken) => {
      submitBookDemoApi(form, {
        loadingButton: btn,
        recaptchaToken: recaptchaToken,
        onSuccess: (f, resultType) => {
          if (resultType === 'success') {
            // store message
            sessionStorage.setItem('thank_you_msg', 'Phone Call created successfully.');
            // Small delay (important)
            setTimeout(() => {
              window.location.href = '/pages/thank-you';
            }, 300);

          } else {
            const os = f.querySelector('[data-book-demo-otp-section]');
            const s1 = f.querySelector('[data-book-demo-step="1"]');

            if (os) os.style.display = 'none';
            if (s1) s1.style.display = '';
          }

        }
      });
    });
  };

  // Fixed anonymous function creation to prevent duplication
  const handleOtpInputChange = (e) => {
    const form = e.target.closest(FORM_SELECTOR);
    if (form) updateVerifyOtpButtonState(form);
  };

  const initSendOtp = (form) => {
    const otpSection = form.querySelector('[data-book-demo-otp-section]');
    if (otpSection) otpSection.style.display = 'none';
    const verifyOtpBtn = form.querySelector('[data-book-demo-verify-otp]');
    const otpInput = form.querySelector('[data-book-demo-otp-input]');
    const resendLink = form.querySelector('[data-book-demo-resend-otp]');
    if (verifyOtpBtn) {
      verifyOtpBtn.removeEventListener('click', handleVerifyOtpClick);
      verifyOtpBtn.addEventListener('click', handleVerifyOtpClick);
    }
    if (resendLink) {
      resendLink.removeEventListener('click', handleResendOtpClick);
      resendLink.addEventListener('click', handleResendOtpClick);
    }
    if (otpInput) {
      updateVerifyOtpButtonState(form);
      otpInput.removeEventListener('input', handleOtpInputChange);
      otpInput.addEventListener('input', handleOtpInputChange);
      otpInput.removeEventListener('change', handleOtpInputChange);
      otpInput.addEventListener('change', handleOtpInputChange);
    }
  };

  const init = () => {
    const forms = document.querySelectorAll(FORM_SELECTOR);
    if (!forms.length) return;

    const urlParams = new URLSearchParams(window.location.search);
    const utmTerm = (urlParams.get('utm_term') || '').toLowerCase();

    forms.forEach((form) => {
      // Very Important: prevent dual-binding if init() fires multiple times
      if (form.dataset.isInitialized === 'true') return;
      form.dataset.isInitialized = 'true';

      form.setAttribute('novalidate', 'novalidate');
      clearErrors(form);
      if (utmTerm === 'water-exchange') {
        const titleEl = form.querySelector('[data-book-demo-title]');
        if (titleEl) titleEl.textContent = 'Exchange Your Water Purifier';
      }
      form.removeEventListener('submit', handleSubmit);
      form.addEventListener('submit', handleSubmit);
      form.addEventListener('input', runFieldValidation);
      form.addEventListener('change', runFieldValidation);
      form.addEventListener('blur', runFieldValidation, true);
      initCityAutocomplete(form);
      initSendOtp(form);
    });

    // Guard global event listeners so they don't compound each script re-evaluation.
    if (typeof window.isGlobalRestrictInitialized === 'undefined') {
      // document.addEventListener('input', (e) => handleRestrictInput(e, 'data-not-allow-alpha', /[a-zA-Z]/g));
      // document.addEventListener('input', (e) => handleRestrictInput(e, 'data-not-allow-number', /\d/g));
      const handleGlobalInput = (e) => {
        handleRestrictInput(e, 'data-not-allow-alpha', /[a-zA-Z]/g );
        handleRestrictInput(e, 'data-not-allow-number', /\d/g);
        const target = e.target;
        if (target.matches('input[data-not-allow-invalid-name="true"]') && target.closest(FORM_SELECTOR)) {
          const value = target.value;
          const cleaned = value.replace(/[^A-Za-z ]/g, '');
          if (value !== cleaned) {
            const position = target.selectionStart;
            target.value = cleaned;
            if (typeof position === 'number') {
              const cursor = Math.min(position, cleaned.length);
              target.setSelectionRange(cursor, cursor);
            }
          }
        }
      };
      if (!window.isGlobalRestrictInitialized) {
        document.addEventListener('input', handleGlobalInput);
        window.isGlobalRestrictInitialized = true;
      }
      window.isGlobalRestrictInitialized = true;
    }
  };

  /** City autocomplete (Google Places, India) – same as partner-form. */
  const initCityAutocomplete = (form) => {
    if (typeof autoComplete === 'undefined') return;
    const inputs = form.querySelectorAll('[data-autocomplete-enabled="true"]');
    inputs.forEach((el) => {
      const autoCompleteEl = new autoComplete({
        selector: () => el,
        data: {
          src: async (query) => {
            if (!window.google || !window.google.maps) return [];
            if (!window.placesService) {
              window.placesService = new google.maps.places.AutocompleteService();
            }
            return new Promise((resolve) => {
              window.placesService.getPlacePredictions(
                { input: query, componentRestrictions: { country: 'in' } },
                (predictions, status) => {
                  if (status !== google.maps.places.PlacesServiceStatus.OK) {
                    resolve([]);
                    return;
                  }
                  resolve(predictions || []);
                }
              );
            });
          },
          keys: ['description']
        },
        threshold: 2,
        searchEngine: (q, r) => r,
        resultsList: {
          element: (list, data) => {
            if (data.results.length === 0) {
              const info = document.createElement('li');
              info.classList.add('not-record');
              info.textContent = 'No results found';
              list.prepend(info);
            }
          },
          noResults: true,
          tabSelect: true
        },
        resultItem: { highlight: true },
        events: {
          input: {
            focus: () => {
              autoCompleteEl.start(autoCompleteEl.input.value || '');
            },
            selection: (event) => {
              const selectionDetail = event.detail && event.detail.selection;
              const value = selectionDetail && selectionDetail.value;
              const feedback = selectionDetail && selectionDetail.feedback;
              let text = '';
              if (typeof value === 'string') {
                  text = value;
              } else if (value && typeof value === 'object' && typeof value.description === 'string') {
                  text = value.description;
              } else if (value && typeof value === 'object') {
                  text = value.description != null ? String(value.description) : (value.main_text || value.secondary_text || '');
              }
              if (!text && feedback && typeof feedback.match === 'string') {
                  text = feedback.match;
              }
              if (text && typeof document !== 'undefined') {
                  const div = document.createElement('div');
                  div.innerHTML = text;
                  text = (div.textContent || div.innerText || text).trim();
              }
              autoCompleteEl.input.value = text || '';
            }
          }
        }
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      document.querySelectorAll(FORM_SELECTOR).forEach(clearErrors);
    }
  });
})();